import React, { useEffect, useState, ReactNode } from "react";
import { Modal } from "react-bootstrap";
import SntButton from "@/cleanup/widgets/forms/SntButton";

export interface SntDialogProps {
  isShow: boolean;
  onSave?: (
    onDialogClicked: () => void,
    onDialogSuccessCallback: () => void,
    onDialogErrorCallback: () => void
  ) => void;
  onClose?: () => void;
  children?: (() => ReactNode) | ReactNode;
  title?: string;
  saveTxt?: string;
  closeTxt?: string;
  minBodyHeight?: string | number;
  height?: string | number;
  width?: string;
  btnSaveClassName?: string;
  isShowCloseButton?: boolean;
  isShowSaveButton?: boolean;
  refWrapper?: React.RefObject<HTMLDivElement | null>;
  backdrop?: boolean | "static";
  customModalClassname?: string;
  bodyClass?: string;
  onHide?: () => void;
  size?: "sm" | "lg" | "xl";
  disabled?: boolean;
  forceUpdateDisable?: boolean | null;
  setForceUpdateDisable?: (value: boolean | null) => void;

  onBack?: () => void;
  backTxt?: string;
  isShowBackButton?: boolean;
  titleExtra?: ReactNode;
}

const SntDialog: React.FC<SntDialogProps> = ({
  isShow,
  onSave,
  onClose,
  children,
  title,
  saveTxt,
  closeTxt,
  minBodyHeight,
  height,
  btnSaveClassName = "sensolus",
  isShowCloseButton = true,
  isShowSaveButton = true,
  refWrapper,
  backdrop = true,
  customModalClassname = "",
  bodyClass,
  onHide,
  size = "lg",
  disabled = false,
  forceUpdateDisable,
  setForceUpdateDisable,
  onBack,
  backTxt,
  isShowBackButton = false,
  titleExtra,
}) => {
  const [confirmClicked, setConfirmClicked] = useState(false);

  const onDialogSuccessCallback = () => {
    setConfirmClicked(false);
  };

  const onDialogErrorCallback = () => {
    setConfirmClicked(false);
  };

  const onDialogClicked = () => {
    setConfirmClicked(true);
  };

  function onSaveClicked() {
    if (onSave) {
      onSave(onDialogClicked, onDialogSuccessCallback, onDialogErrorCallback);
    }
  }

  function onCloseClicked() {
    if (onClose) {
      onClose();
    }
  }

  function onBackClicked() {
    if (onBack) {
      onBack();
    }
  }

  const onHideClicked = () => {
    if (onHide) {
      onHide();
    } else if (onClose) {
      onClose();
    }
  };

  useEffect(() => {
    setConfirmClicked(false);
  }, [isShow]);

  useEffect(() => {
    if (forceUpdateDisable == null) return;
    setConfirmClicked(forceUpdateDisable);
    if (setForceUpdateDisable) {
      setForceUpdateDisable(null);
    }
  }, [forceUpdateDisable, setForceUpdateDisable]);

  return (
    <Modal
      show={isShow}
      dialogClassName="modal-dialog-scrollable"
      onHide={onHideClicked}
      size={size}
      backdrop={backdrop}
      className={`${customModalClassname}`}
    >
      <div ref={refWrapper} className="modal-content">
        <Modal.Header closeButton={isShowCloseButton}>
          <Modal.Title className={titleExtra ? "d-flex align-items-center gap-2" : ""}>
            {title}
            {titleExtra}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body
          className={`${bodyClass || ""} snt-scroll`}
          style={{ minHeight: minBodyHeight, height: height }}
        >
          {isShow && children ? (
            typeof children === "function" ? (
              children()
            ) : (
              children
            )
          ) : (
            <></>
          )}
        </Modal.Body>

        {(isShowSaveButton || isShowBackButton || isShowCloseButton) && (
          <Modal.Footer className="d-flex justify-content-start">
            {isShowBackButton && (
              <SntButton
                variant="outline-sensolus"
                onClick={onBackClicked}
                text={backTxt || "Back"}
              />
            )}
            {isShowSaveButton && (
              <SntButton
                variant={btnSaveClassName}
                onClick={onSaveClicked}
                disabled={disabled || confirmClicked}
                text={saveTxt || "Save"}
              />
            )}
            {isShowCloseButton && (
              <SntButton
                variant="outline-sensolus"
                onClick={onCloseClicked}
                text={closeTxt || "Close"}
              />
            )}
          </Modal.Footer>
        )}
      </div>
    </Modal>
  );
};

export default SntDialog;
